import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import {
  applyFieldResponseMetadata,
  dropStaleChoiceSelections,
  deriveComputedValueKey,
  resolveDatabasePrepopulation,
} from "../../utils";
import { useCallback, useMemo, useRef, useState } from "react";
import { FormTypesWithValues, TellescopeFieldResponse } from "../../types";
import {
  AnswerForType,
  DatabaseRecord,
  Enduser,
  FormResponseValue,
  FormResponseValueAnswer,
} from "@tellescope/types-models";
import { usePendingFileUploads } from "./use-pending-file-uploads";
import type { FormShallowStoreHook } from "../../store";
import { useSubmitPhase } from "./use-submit-phase";
import { resolveFieldValidity } from "./resolve-field-validity";
import { useResolveCurrentResponses } from "./use-resolve-current-responses";
import { isRepeatMismatch } from "./resolve-repeat-mismatch";
import { useFieldValidation } from "./use-field-validation";
import { useFormDataQueries } from "./use-form-data-queries";
import { useForgetAnsweredFields } from "./use-forget-answered-fields";
import { useTouchedFields } from "./use-touched-fields";
import { useVisibleFields } from "./use-visible-fields";
import { useSaveResponse } from "./use-save-response";
import { isFormComplete } from "./is-form-complete";
import { useInterpolateFieldText } from "./use-interpolate-field-text";

export type LocalTellescopeFormResponseMap = Record<string, TellescopeFieldResponse>;

export interface UseTellescopeFormCoreArgs {
  formId: string;
  formTitle?: string;
  responseId?: string;
  enduserId: string;
  parentResponseId?: string;
  rootResponseId?: string;
  existingResponses?: FormResponseValue[];
  accessCode?: string;
  enduser?: TellescopeModelFromName<"endusers"> | Enduser;
  urlLogicValue?: string;
  publicIdentifier?: string;
  useFormShallowStore: FormShallowStoreHook;
}

/**
 * Internal hook containing shared logic for both single-page and enduser form flows.
 * Not intended for direct consumption — use `useTellescopeSinglePageForm` or `useTellescopeEnduserForm`.
 */
export const useTellescopeFormCore = ({
  formId,
  formTitle: providedFormTitle,
  responseId,
  enduserId,
  parentResponseId,
  rootResponseId,
  existingResponses,
  accessCode,
  enduser,
  urlLogicValue: providedUrlLogicValue,
  publicIdentifier,
  useFormShallowStore,
}: UseTellescopeFormCoreArgs) => {
  const resolveCurrentResponses = useResolveCurrentResponses({ publicIdentifier });

  const {
    form,
    fields,
    response,
    formTitle,
    responsesMap,
    enduserForPrepopulation,
    formQuery,
    formFieldsQuery,
    responseQuery,
    enduserResponseInfoQuery,
    enduserQuery,
  } = useFormDataQueries({ formId, providedFormTitle, responseId, enduserId, accessCode, enduser });

  // The write target of setFieldValue and the top of the currentResponses precedence chain.
  const [unsavedResponses, setUnsavedResponses] = useState<LocalTellescopeFormResponseMap>({});
  const validateFieldRef = useRef<(args: { fieldId: string }) => Promise<string | null>>(() => Promise.resolve(null));
  const {
    touchedFields,
    setTouchedFields,
    repeatInputs,
    setRepeatValue,
    repeatTouchedFields,
    handleFieldBlur,
    handleRepeatBlur,
    markFieldTouched,
  } = useTouchedFields({ validateFieldRef, useFormShallowStore });
  const forgetAnsweredFields = useForgetAnsweredFields({ useFormShallowStore });

  const currentResponses = useMemo(
    () =>
      resolveCurrentResponses({
        fieldsArr: fields?.fieldsArr,
        unsavedResponses,
        responsesMap,
        existingResponses,
        enduserForPrepopulation,
        form,
      }),
    [
      fields?.fieldsArr,
      unsavedResponses,
      responsesMap,
      existingResponses,
      enduserForPrepopulation,
      form,
      resolveCurrentResponses,
    ]
  );

  // Set by whichever surface owns this store — the deeplink reads it from the query string. Falls
  // back to the argument for callers that still pass it directly.
  const { logic: storedUrlLogicValue } = useFormShallowStore("logic");

  const currentResponsesRef = useRef(currentResponses);
  currentResponsesRef.current = currentResponses;

  const { pendingFiles, setPendingFilesForField, uploadPendingFiles, uploadProgress } = usePendingFileUploads({
    fieldsMap: fields?.fieldsMap,
    currentResponses,
    enduserId,
    setUnsavedResponses,
    useFormShallowStore,
  });

  const uncommittedValuesRef = useRef<Record<string, FormResponseValueAnswer["value"]>>({});
  uncommittedValuesRef.current = Object.entries(uncommittedValuesRef.current).reduce<
    Record<string, FormResponseValueAnswer["value"]>
  >((acc, [fieldId, value]) => {
    const committed = currentResponses[fieldId]?.answer.value;
    if (JSON.stringify(committed) !== JSON.stringify(value)) acc[fieldId] = value;
    return acc;
  }, {});

  const repeatMismatchFieldIds = useMemo(
    () =>
      Object.values(currentResponses).reduce<Set<string>>((acc, response) => {
        const field = fields?.fieldsMap[response.fieldId];
        if (!field) return acc;
        if (
          isRepeatMismatch({ field, value: response.answer?.value, confirmation: repeatInputs[response.fieldId] ?? "" })
        )
          acc.add(response.fieldId);
        return acc;
      }, new Set()),
    [currentResponses, fields?.fieldsMap, repeatInputs]
  );

  const { logicOptions, subFieldIds, isSubFieldHidden, visibleFields } = useVisibleFields({
    form,
    fieldsArr: fields?.fieldsArr,
    fieldsMap: fields?.fieldsMap,
    currentResponses,
    urlLogicValue: storedUrlLogicValue ?? providedUrlLogicValue,
    enduser: enduserQuery.data,
    useFormShallowStore,
  });

  const { fieldErrors, updateFieldErrors, validateField, validateAllFields } = useFieldValidation({
    fieldsMap: fields?.fieldsMap,
    fieldsArr: fields?.fieldsArr,
    currentResponses,
    currentResponsesRef,
    uncommittedValuesRef,
    pendingFiles,
    repeatInputs,
    isSubFieldHidden,
    setTouchedFields,
    useFormShallowStore,
  });

  validateFieldRef.current = validateField;

  const setFieldValue = useCallback(
    <TField extends FormTypesWithValues>(fieldId: string, value: AnswerForType[TField]) => {
      uncommittedValuesRef.current[fieldId] = value;

      const field = fields?.fieldsMap[fieldId];
      const originalResponse = currentResponsesRef.current[fieldId] ?? {
        fieldId,
        fieldTitle: field?.title ?? "",
        answer: {
          type: field?.type,
          value: undefined,
        },
      };
      if (
        value !== undefined &&
        originalResponse?.answer.value !== undefined &&
        typeof value !== typeof originalResponse?.answer.value
      ) {
        return;
      }

      const newAnswer = {
        ...originalResponse?.answer,
        value,
      } as FormResponseValueAnswer;
      const newResponse: FormResponseValue = applyFieldResponseMetadata(
        {
          ...originalResponse,
          answer: newAnswer,
          computedValueKey: deriveComputedValueKey(field?.intakeField, newAnswer),
        },
        field
      );

      // Swept against the ref rather than inside the state updater: React only runs an updater
      // eagerly when no update is already queued, so reading the emptied ids after the call
      // dropped them — and the cleanup below — whenever an update was in flight.
      const merged = { ...currentResponsesRef.current, [fieldId]: newResponse };
      const swept = dropStaleChoiceSelections({
        responses: merged,
        fieldsArr: fields?.fieldsArr ?? [],
        changedFieldId: fieldId,
        logicOptions,
      });

      // Only the changed field and whatever the sweep rewrote: `merged` also holds saved and
      // prepopulated answers, which must not be promoted into unsaved state.
      const changed = Object.entries(swept.responses).reduce<LocalTellescopeFormResponseMap>(
        (acc, [id, response]) => (response === merged[id] && id !== fieldId ? acc : { ...acc, [id]: response }),
        {}
      );

      setUnsavedResponses((prev) => ({ ...prev, ...changed }));

      if (swept.emptiedFieldIds.size) forgetAnsweredFields(swept.emptiedFieldIds);
    },
    [fields?.fieldsMap, fields?.fieldsArr, logicOptions, forgetAnsweredFields]
  );

  const applyDatabasePrepopulation = useCallback(
    (records: DatabaseRecord[]) => {
      const prepopulated = resolveDatabasePrepopulation({
        fieldsArr: fields?.fieldsArr ?? [],
        records,
        currentResponses: currentResponsesRef.current,
      });
      if (!Object.keys(prepopulated).length) return;
      setUnsavedResponses((prev) => ({ ...prev, ...prepopulated }));
    },
    [fields?.fieldsArr]
  );

  const { customerId, setCustomerId, bookedAppointmentUrl, setBookedAppointmentUrl } = useFormShallowStore(
    "customerId",
    "setCustomerId",
    "bookedAppointmentUrl",
    "setBookedAppointmentUrl"
  );

  const isFieldValueValid = useCallback(
    (fieldId: string): boolean => {
      const field = fields?.fieldsMap[fieldId];
      if (!field) return true;

      const { valid, error } = resolveFieldValidity({
        field,
        fieldsMap: fields?.fieldsMap,
        currentResponses,
        pendingFiles,
        isSubFieldHidden,
      });

      if (error !== undefined) updateFieldErrors((prev) => ({ ...prev, [field.id]: error }));

      return valid;
    },
    [fields?.fieldsMap, currentResponses, pendingFiles, isSubFieldHidden, updateFieldErrors]
  );

  const { prepareFormResponseMutation, preparedResponse, submitFormMutation, saveFieldResponseMutation, saveResponse } =
    useSaveResponse({
      formId,
      enduserId,
      responseId,
      parentResponseId,
      rootResponseId,
      formTitle,
      response,
      currentResponses,
    });

  const { submitPhase, runSubmit } = useSubmitPhase({ uploadPendingFiles, useFormShallowStore });

  const interpolateFieldText = useInterpolateFieldText({
    enduser: accessCode ? enduser : enduserQuery.data,
    currentResponses,
  });

  const isComplete = useMemo(
    () =>
      isFormComplete({
        visibleFields,
        fieldsMap: fields?.fieldsMap,
        currentResponses,
        fieldErrors,
        logicOptions,
      }),
    [visibleFields, fields?.fieldsMap, currentResponses, fieldErrors, logicOptions]
  );

  return {
    form,
    formQuery,
    formFieldsQuery,
    responseQuery,
    enduser: accessCode ? enduser : enduserQuery.data,
    enduserResponseInfoQuery,
    formTitle,
    prepareFormResponseMutation,
    preparedResponse,
    submitFormMutation,
    saveFieldResponseMutation,
    currentResponses,
    responsesMap,
    interpolateFieldText,
    fieldErrors,
    touchedFields,
    logicOptions,
    visibleFields,
    isComplete,
    validateField,
    validateAllFields,
    setFieldValue,
    handleFieldBlur,
    markFieldTouched,
    saveResponse,
    fields,
    subFieldIds,
    isSubFieldHidden,
    repeatInputs,
    repeatTouchedFields,
    setRepeatValue,
    handleRepeatBlur,
    repeatMismatchFieldIds,
    isFieldValueValid,
    pendingFiles,
    setPendingFilesForField,
    applyDatabasePrepopulation,
    customerId,
    setCustomerId,
    bookedAppointmentUrl,
    setBookedAppointmentUrl,
    uploadPendingFiles,
    runSubmit,
    submitPhase,
    uploadProgress,
  };
};
